import { TempoFilterConfig } from 'constants/filters';
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import styled from 'styled-components';
import { Box } from '@landr/maestro';
import { Range } from 'types';
import Draggable from './Draggable';

type TempoSliderControllerProps = {
    range: Range;
    isExactBpm: boolean;
    onChange: (range: Range) => void;
};

interface SliderActiveBpmRailProps {
    horizontalPosition: number;
    minBpmPosX: number;
    maxBpmPosX: number;
}

const SliderContent = styled(Box)`
    width: 100%;
    padding: ${({ theme }): string =>
        `${theme.spacing(6)} ${theme.spacing(4)}`};
    float: left;
    position: relative;
    user-select: none;
`;

const InsideWrapper = styled(Box)`
    position: absolute;
    width: calc(100% - 28px);
    left: 14px;
`;

const SliderRailBackground = styled(Box)`
    width: 100%;
    height: 2px;
    background-color: ${({ theme: { palette } }): string =>
        palette.neutral[600]};
`;

const SliderActiveBpmRail = styled.div.attrs<SliderActiveBpmRailProps>(
    ({ minBpmPosX, maxBpmPosX, horizontalPosition }) => ({
        style: {
            width: `${maxBpmPosX - minBpmPosX + 4}px`,
            transform: `translateX(${minBpmPosX + 10}px)`,
        },
    }),
)<SliderActiveBpmRailProps>`
    height: 4px;
    background-color: ${({ theme: { palette } }): string =>
        palette.primary[500]};
    position: absolute;
    margin-top: -3px;
`;

export const getSliderPositionFromValue = (
    value: number,
    maxSliderPosX: number,
): number => {
    const ratio =
        (value - TempoFilterConfig.MinBpm) /
        (TempoFilterConfig.MaxBpm - TempoFilterConfig.MinBpm);
    const newPosXFromBpmPercent = maxSliderPosX * ratio;
    const roundedResult = newPosXFromBpmPercent.toFixed(2);

    return parseInt(roundedResult, 10);
};

export const getValueFromSliderPosition = (
    x: number,
    maxSliderPosX: number,
): number => {
    const ratio = x / (maxSliderPosX - TempoFilterConfig.ButtonRadius / 2);
    let result =
        (TempoFilterConfig.MaxBpm - TempoFilterConfig.MinBpm) * ratio +
        TempoFilterConfig.MinBpm;

    if (result > TempoFilterConfig.MaxBpm) {
        result = TempoFilterConfig.MaxBpm;
    } else if (result < TempoFilterConfig.MinBpm) {
        result = TempoFilterConfig.MinBpm;
    }

    const roundedResult = result.toFixed(2);

    return Math.round(parseInt(roundedResult, 10));
};

const TempoSliderController: React.FC<TempoSliderControllerProps> = ({
    range,
    isExactBpm,
    onChange,
}) => {
    const [sliderRef, setSliderRef] = useState();
    const railBackground = useRef<HTMLDivElement>(sliderRef as any);
    const [responsiveMaxPosXRatio, setResponsiveMaxPosXRatio] = useState(0);
    const rangePadding = 5;

    useEffect(() => {
        if (railBackground.current) {
            setSliderRef(railBackground.current as any);
            if (railBackground.current.clientWidth !== 0) {
                setResponsiveMaxPosXRatio(
                    railBackground.current.clientWidth -
                        TempoFilterConfig.ButtonRadius ||
                        TempoFilterConfig.MaxSliderPosX -
                            TempoFilterConfig.ButtonRadius,
                );
            }
        }
    }, [sliderRef, responsiveMaxPosXRatio]);

    const { MinSliderPosX } = TempoFilterConfig;
    const maxSliderPosX =
        responsiveMaxPosXRatio !== 0
            ? responsiveMaxPosXRatio
            : TempoFilterConfig.MaxSliderPosX;

    const [positions, setPositions] = useState<Range>({
        min: MinSliderPosX,
        max: maxSliderPosX,
    });

    const minBpmPosX = useMemo(
        () =>
            getSliderPositionFromValue(
                range.min,
                maxSliderPosX - TempoFilterConfig.ButtonRadius / 2,
            ),
        [range.min, maxSliderPosX],
    );
    const maxBpmPosX = useMemo(
        () =>
            getSliderPositionFromValue(
                range.max,
                maxSliderPosX - TempoFilterConfig.ButtonRadius / 2,
            ),
        [range.max, maxSliderPosX],
    );

    const getMinSliderPosX = useMemo(
        () => positions.min + TempoFilterConfig.MinSliderPosX * 2,
        [positions.min],
    );
    const getMaxSliderPosX = useMemo(
        () => positions.max - TempoFilterConfig.MinSliderPosX * 2,
        [positions.max],
    );

    useEffect(() => {
        const minX = minBpmPosX;
        const maxX = maxBpmPosX;

        setPositions({ min: minX, max: maxX });
    }, [range.min, range.max, minBpmPosX, maxBpmPosX]);

    const setMinValue = useCallback(
        (x: number) => {
            const minValue = getValueFromSliderPosition(x, maxSliderPosX);

            if (isExactBpm) {
                onChange({ min: minValue, max: minValue });
            } else {
                onChange({ min: minValue, max: range.max });
            }
        },
        [isExactBpm, onChange, range.max, maxSliderPosX],
    );

    const setMaxValue = useCallback(
        (x: number) => {
            const maxValue = getValueFromSliderPosition(x, maxSliderPosX);

            onChange({ min: range.min, max: maxValue });
        },
        [onChange, range.min, maxSliderPosX],
    );

    return (
        <SliderContent>
            <InsideWrapper>
                <SliderRailBackground ref={railBackground} />
                {!isExactBpm && (
                    <SliderActiveBpmRail
                        horizontalPosition={0}
                        minBpmPosX={minBpmPosX}
                        maxBpmPosX={maxBpmPosX}
                    />
                )}
                <Draggable
                    horizontalPosition={TempoFilterConfig.MinSliderPosX}
                    draggableRadius={TempoFilterConfig.DraggableZoneRadius}
                    styledButtonRadius={TempoFilterConfig.ButtonRadius}
                    onChange={(posX) => {
                        setMinValue(posX);
                    }}
                    minSliderPosX={MinSliderPosX - rangePadding}
                    maxSliderPosX={getMaxSliderPosX}
                    imperativePosition={positions.min}
                    maxRange={isExactBpm ? maxSliderPosX : getMaxSliderPosX}
                    data-testid="minBpm-draggable-testid"
                    isSingleValue={true}
                />
                {!isExactBpm && (
                    <Draggable
                        horizontalPosition={maxBpmPosX}
                        draggableRadius={TempoFilterConfig.DraggableZoneRadius}
                        styledButtonRadius={TempoFilterConfig.ButtonRadius}
                        onChange={(posX) => setMaxValue(posX)}
                        minSliderPosX={getMinSliderPosX}
                        maxSliderPosX={maxSliderPosX}
                        imperativePosition={positions.max}
                        maxRange={maxSliderPosX}
                        data-testid="maxBpm-draggable-testid"
                        isSingleValue={false}
                    />
                )}
            </InsideWrapper>
        </SliderContent>
    );
};

export default TempoSliderController;
